import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import RequireRootRole from './components/RequireRootRole';
import HelpIcon from '../../../../components/help/HelpIcon';
import ChartConfigService from '../../../../services/ChartConfigService';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

const monoTextFieldStyle = {
  ...darkTextFieldStyle,
  '& .MuiOutlinedInput-input': { fontFamily: 'monospace', fontSize: '0.85rem' }
};

const REQUIRED_PLACEHOLDERS = ['<<answer>>', '<<data_sources>>'];
const REQUIRED_TAGS = [
  '<has_chart>', '</has_chart>', '<chart_type>', '</chart_type>',
  '<chart_configuration>', '</chart_configuration>', '<caption>', '</caption>',
  '<rationale>', '</rationale>'
];

const emptyForm = { prompt_template: '', model_id: '', max_tokens: '', temperature: '' };

// Chequeo rápido en cliente, no es el gate real (eso lo hace el backend al
// guardar) -- solo evita un viaje redondo obvio antes de mandar el PUT.
const findMissingPromptRequirements = (promptTemplate) => {
  const missingPlaceholders = REQUIRED_PLACEHOLDERS.filter((p) => !promptTemplate.includes(p));
  const missingTags = REQUIRED_TAGS.filter((t) => !promptTemplate.includes(t));
  return [...missingPlaceholders, ...missingTags];
};

const ChartGenerationSettingsContent = () => {
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await ChartConfigService.getConfig();
      setForm({
        prompt_template: data.prompt_template || '',
        model_id: data.model_id || '',
        max_tokens: data.max_tokens != null ? String(data.max_tokens) : '',
        temperature: data.temperature != null ? String(data.temperature) : ''
      });
      setMeta({ updated_at: data.updated_at || null, updated_by: data.updated_by || null });
    } catch (err) {
      setError(err.message || 'No fue posible obtener la configuración de generación de gráficas.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    const missing = findMissingPromptRequirements(form.prompt_template);
    if (missing.length > 0) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: `Al prompt le falta: ${missing.join(', ')}. El modelo no va a poder generar la gráfica sin esto.`
      });
      return;
    }
    if (!form.model_id.trim() || form.max_tokens === '' || form.temperature === '') {
      setSnackbar({ open: true, severity: 'error', message: 'model_id, max_tokens y temperature son requeridos.' });
      return;
    }

    try {
      setIsSaving(true);
      await ChartConfigService.upsertConfig({
        prompt_template: form.prompt_template,
        model_id: form.model_id.trim(),
        max_tokens: Number(form.max_tokens),
        temperature: Number(form.temperature)
      });
      setSnackbar({ open: true, severity: 'success', message: 'Configuración guardada correctamente.' });
      load();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message || 'No se pudo guardar la configuración.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer
      title="Generación de gráficas"
      description='Prompt y parámetros de Bedrock que usa la generación automática de gráficas (pestaña "Gráfica" del chat). Los cambios aplican en la siguiente consulta, sin necesidad de desplegar nada.'
      actions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={isLoading}
          sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}
        >
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: 'var(--wb-primary)' }} />
        </Box>
      ) : (
        <>
          <Section
            title="Prompt de generación"
            subtitle="Debe conservar los placeholders <<answer>> / <<data_sources>> y los tags <has_chart>/<chart_type>/<chart_configuration>/<caption>/<rationale> — el backend los usa para interpretar la respuesta del modelo."
          >
            <TextField
              value={form.prompt_template}
              onChange={(e) => setForm((prev) => ({ ...prev, prompt_template: e.target.value }))}
              multiline
              minRows={20}
              fullWidth
              sx={monoTextFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="sistema" topic="generacion-de-graficas" anchor="prompt-de-generación" />
                  </InputAdornment>
                )
              }}
            />
          </Section>

          <Section title="Parámetros del modelo">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="model_id (inference profile de Bedrock)"
                  value={form.model_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, model_id: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={darkTextFieldStyle}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <HelpIcon module="sistema" topic="generacion-de-graficas" anchor="model_id-inference-profile-de-bedrock" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="max_tokens"
                  type="number"
                  value={form.max_tokens}
                  onChange={(e) => setForm((prev) => ({ ...prev, max_tokens: e.target.value }))}
                  inputProps={{ min: 1, step: 1 }}
                  fullWidth
                  size="small"
                  sx={darkTextFieldStyle}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <HelpIcon module="sistema" topic="generacion-de-graficas" anchor="max_tokens" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="temperature"
                  type="number"
                  value={form.temperature}
                  onChange={(e) => setForm((prev) => ({ ...prev, temperature: e.target.value }))}
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  fullWidth
                  size="small"
                  sx={darkTextFieldStyle}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <HelpIcon module="sistema" topic="generacion-de-graficas" anchor="temperature" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mt: 3 }}>
              <Typography sx={{ color: 'var(--wb-muted-70)' }}>
                {meta.updated_at
                  ? `Última actualización: ${meta.updated_at}${meta.updated_by ? ` por ${meta.updated_by}` : ''}`
                  : 'Todavía nadie guardó una configuración propia — se está usando el valor por defecto de la plataforma.'}
              </Typography>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
                sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' }, mt: { xs: 2, sm: 0 } }}
              >
                Guardar
              </Button>
            </Stack>
          </Section>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success' ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning' ? 'var(--wb-warning-90)'
                  : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

const ChartGenerationSettings = () => (
  <RequireRootRole title="Generación de gráficas">
    <ChartGenerationSettingsContent />
  </RequireRootRole>
);

export default ChartGenerationSettings;
